import { Component, HostBinding, Input } from '@angular/core';
import { AgWidgetConfig, AskAIConfig, isNotNullOrUndefined, keysOf, taggedNamespace } from '@mri-platform/shared/core';
import { AuthService } from '@mri/angular-wfe-proxy-oidc';
import { DialogCloseResult, DialogService } from '@progress/kendo-angular-dialog';
import { RxState } from '@rx-angular/state';
import { selectSlice } from '@rx-angular/state/selections';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { AskAgoraFrameMenuItem, HeaderLogo, isAskAgoraFrameComponent, MenuItem } from '../models';
import { getCurrentClientId$, MainNavService, WidgetService } from '../services';

interface ComponentState {
  clientId: string;
  menuItems: MenuItem[];
  activeItem: MenuItem | null;
  headerLogo: HeaderLogo | null;
  isNavExpanded: boolean;
  renderWidget: boolean;
  agWidgetLoaded: boolean;
  askAiWidgetLoaded: boolean;
  askAiFrameWidgetLoaded: boolean;
  activePage: MenuItem | null;
  enableAskAgora: boolean;
  askAgoraConfig: AskAIConfig | null;
  askAgoraDockingEnabled: boolean;
  askAgoraWidgetOpen: boolean;
}

interface Projections {
  showWidget: boolean;
  showAskAiFrameButton: boolean;
  askAiFrameMenuItem: AskAgoraFrameMenuItem | null;
  showAskAgoraWidget: boolean;
}

type PublicState = Omit<ComponentState, 'renderWidget'>;

type ViewModel = PublicState & Projections;

const initialPublicState: PublicState = {
  clientId: '',
  menuItems: [],
  activeItem: null,
  activePage: null,
  headerLogo: null,
  isNavExpanded: false,
  agWidgetLoaded: false,
  askAiWidgetLoaded: false,
  askAiFrameWidgetLoaded: false,
  enableAskAgora: false,
  askAgoraConfig: null,
  askAgoraDockingEnabled: false,
  askAgoraWidgetOpen: false
};

const initialState: ComponentState = {
  ...initialPublicState,
  renderWidget: true
};

@Component({
  selector: 'mri-shared-main-nav-bar',
  templateUrl: './main-nav-bar.component.html',
  styleUrls: ['./main-nav-bar.component.scss'],
  providers: [RxState]
})
export class MainNavBarComponent {
  @HostBinding('attr.data-testid') testId = 'MainNavBarComponent';

  public widgetOpen = false;
  @HostBinding('class.mri-menu-bar') hostClass = true;

  @HostBinding('class.expanded') get isExpanded() {
    return this.state.get('isNavExpanded');
  }

  @Input() set headerLogo(headerLogo: HeaderLogo | null) {
    this.state.set({ headerLogo });
  }

  @Input() set renderWidget(renderWidget: boolean) {
    this.state.set({ renderWidget });
  }

  @Input() set menuItems(menuItems: MenuItem[]) {
    this.mainNavService.menuItems = menuItems;
  }

  @Input() set enableAskAgora(enableAskAgora: boolean) {
    this.state.set({ enableAskAgora });
  }

  @Input() set askAgoraConfig(askAgoraConfig: AskAIConfig | null) {
    this.state.set({ askAgoraConfig });
  }

  @Input() set askAgoraDockingEnabled(askAgoraDockingEnabled: boolean) {
    this.state.set({ askAgoraDockingEnabled });
  }

  @Input() detailedErrorMessagesWidget = false;

  vm$: Observable<ViewModel>;

  apiHost = this.agWidgetConfig.baseApiUrl;

  constructor(
    private readonly mainNavService: MainNavService,
    private readonly state: RxState<ComponentState>,
    private readonly agWidgetConfig: AgWidgetConfig,
    private readonly widgetService: WidgetService,
    private readonly authService: AuthService,
    private readonly dialogService: DialogService
  ) {
    const tag = taggedNamespace(this.testId);

    // Set initial state
    this.state.set(initialState);

    // Connect any observable-driven items to state for items in ComponentState...
    const menuItems$ = this.mainNavService.menuItems$.pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.state.connect('menuItems', menuItems$);
    this.state.connect('activeItem', this.mainNavService.activeItem$);
    this.state.connect('activePage', this.mainNavService.activePage$);
    this.state.connect('clientId', getCurrentClientId$(this.authService, this.mainNavService));
    this.state.connect('agWidgetLoaded', this.widgetService.loadWidget$());
    this.state.connect('isNavExpanded', this.mainNavService.isExpanded$);

    // Load Ask Agora widget when config is provided
    const askAgoraWidgetLoaded$ = this.state.select('askAgoraConfig').pipe(
      filter(config => !!config?.cdnUrl),
      take(1),
      switchMap(config => this.widgetService.loadAskAiWidget$(config!.cdnUrl))
    );
    this.state.connect('askAiWidgetLoaded', askAgoraWidgetLoaded$);

    const askAiFrameMenuItem$ = menuItems$.pipe(map(items => items.find(isAskAgoraFrameComponent) ?? null));

    const askAiFrameWidgetLoaded$ = askAiFrameMenuItem$.pipe(
      isNotNullOrUndefined(),
      take(1),
      switchMap(askAiFrameMenuItem => widgetService.loadAskAiFrameWidget$(askAiFrameMenuItem.cdnUrl))
    );
    this.state.connect('askAiFrameWidgetLoaded', askAiFrameWidgetLoaded$);

    // Create projections (calculations from ComponentState)...
    const showWidget$ = this.state
      .select(selectSlice(['renderWidget', 'agWidgetLoaded', 'clientId']))
      .pipe(map(({ renderWidget, agWidgetLoaded, clientId }) => renderWidget && agWidgetLoaded && !!clientId));

    const showAskAiFrameButton$ = this.state
      .select(selectSlice(['askAiFrameWidgetLoaded', 'clientId']))
      .pipe(map(({ askAiFrameWidgetLoaded, clientId }) => askAiFrameWidgetLoaded && !!clientId));

    const showAskAgoraWidget$ = this.state
      .select(selectSlice(['enableAskAgora', 'askAiWidgetLoaded', 'askAgoraConfig', 'clientId']))
      .pipe(
        map(
          ({ enableAskAgora, askAiWidgetLoaded, askAgoraConfig, clientId }) =>
            enableAskAgora && askAiWidgetLoaded && !!askAgoraConfig && !!clientId
        )
      );

    // Create ViewModel (Projections + PublicState)...
    const publicState$ = this.state.select(selectSlice(keysOf(initialPublicState)));

    this.vm$ = combineLatest([
      publicState$,
      showWidget$,
      showAskAiFrameButton$,
      showAskAgoraWidget$,
      askAiFrameMenuItem$
    ]).pipe(
      map(([publicState, showWidget, showAskAiFrameButton, showAskAgoraWidget, askAiFrameMenuItem]) => ({
        ...publicState,
        showWidget,
        showAskAiFrameButton,
        showAskAgoraWidget,
        askAiFrameMenuItem
      })),
      tag('vm'),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    // side effects if any...
    // none
  }

  itemHref(menuItem: MenuItem) {
    return menuItem.customIcon
      ? `assets/icons/ui-icons.svg#${menuItem.customIcon}-icon`
      : `assets/@mri/svg-icons/mri-icon-sprite.svg#${menuItem.iconName}`;
  }

  clickIcon(activeItem: MenuItem) {
    // Handle mega menu interactions while keeping Ask Agora open
    const askAgoraOpen = this.state.get('askAgoraWidgetOpen');
    this.mainNavService.activate(activeItem);

    // If Ask Agora is open and user clicks a menu item, keep Ask Agora open
    // This allows mega menu to overlay Ask Agora as specified
    if (askAgoraOpen) {
      // Keep Ask Agora widget open when navigating menu
      this.state.set({ askAgoraWidgetOpen: true });
    }
  }

  toggleNavExpansion() {
    const isNavExpanded = this.state.get('isNavExpanded');
    this.mainNavService.setExpanded(!isNavExpanded);
  }

  logout() {
    const dialog = this.dialogService.open({
      cssClass: 'mri-k-dialog--actions-margin-fix', // todo: remove this after fix has been implemented in the latest @mri/kendo-theme package
      title: 'Sign out',
      content: `Are you sure you want to sign out?`,
      actions: [{ text: 'Sign out', themeColor: 'primary' }, { text: 'Cancel' }],
      width: '75%',
      maxWidth: 500
    });

    const result = dialog.result
      .pipe(
        filter(result => !(result instanceof DialogCloseResult) && result && result.text === 'Sign out'),
        tap(() => this.authService.logOut()),
        finalize(() => result.unsubscribe())
      )
      .subscribe();
  }

  widgetClicked() {
    this.widgetOpen = !this.widgetOpen;
    this.mainNavService.setExpanded(false);
  }

  onAskAgoraFrameClosed(activeItem: MenuItem | null) {
    if (activeItem && isAskAgoraFrameComponent(activeItem)) {
      this.mainNavService.activate(activeItem);
    }
  }

  async toggleAskAgoraWidget() {
    const currentState = this.state.get('askAgoraWidgetOpen');

    if (currentState) {
      // Widget is open (either floating or minimized), close it entirely
      await this.closeWidgetCompletely();
    } else {
      // Widget is closed, open it normally
      this.state.set({ askAgoraWidgetOpen: true });
    }
  }

  private async closeWidgetCompletely() {
    // Look for the minimized widget first and try to click its close button
    const minimizedWidget = document.querySelector('ask-ai-minimized');

    if (minimizedWidget) {
      const shadowRoot = minimizedWidget.shadowRoot;
      if (shadowRoot) {
        const closeButton = shadowRoot.querySelector('.minimized-close-button');
        if (closeButton) {
          (closeButton as HTMLElement).click();
          return;
        }
      }

      // If no close button found, try triggering the close event directly
      if (typeof (minimizedWidget as any).close === 'object' && (minimizedWidget as any).close.emit) {
        (minimizedWidget as any).close.emit();
        return;
      }
    }

    // Widget is not minimized, close normally
    this.state.set({ askAgoraWidgetOpen: false });
  }

  onAskAgoraWidgetClosed() {
    this.state.set({ askAgoraWidgetOpen: false });
  }
}
